'use client';

import { useState } from 'react';
import { Bot } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { SkillSettingDTO } from '../store';

interface SkillSettingsDialogProps {
  skillName: string;
  setting: SkillSettingDTO;
  onConfirm: (value: number) => void;
  onClose: () => void;
}

export function SkillSettingsDialog({ skillName, setting, onConfirm, onClose }: SkillSettingsDialogProps) {
  const t = useTranslations('skills');
  const [value, setValue] = useState(setting.value);
  const [aiSuggestion] = useState<string | null>(null);

  const keyLabel = setting.key.replace(/_/g, ' ');

  // TODO: wire to real /api/v1/chat/suggest endpoint (FE-09 or later)
  const AI_ENABLED = false;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogOverlay onClick={onClose} />
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('skillSettingsDialog.title', { setting: keyLabel })}</DialogTitle>
          <DialogDescription>
            {t.rich('skillSettingsDialog.description', {
              setting: keyLabel,
              skillName,
              medium: (chunks) => <span className="font-medium">{chunks}</span>,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {setting.description && (
            <p className="text-sm text-muted-foreground">{setting.description}</p>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="setting-value" className="text-sm font-medium capitalize">
              {keyLabel}
            </label>
            <input
              id="setting-value"
              type="number"
              value={value}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) setValue(v);
              }}
              className={cn(
                'w-full rounded-md border border-border bg-surface px-3 py-2',
                'text-sm font-medium',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                'transition-colors duration-150',
              )}
              min={0}
            />
            <span className="text-xs text-muted-foreground">
              {t('skillSettingsDialog.default', { value: setting.default })}
            </span>
          </div>

          <div className="rounded-md border border-border bg-surface/50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">{t('skillSettingsDialog.aiRecommendation')}</span>
              <button
                type="button"
                disabled={!AI_ENABLED}
                title={t('skillSettingsDialog.aiComingSoonTooltip')}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2 py-1',
                  'text-xs font-medium text-accent border border-accent/30',
                  'hover:bg-accent/10 transition-colors duration-150',
                  'disabled:opacity-50 disabled:pointer-events-none',
                )}
              >
                <Bot className="h-3 w-3" />
                {t('skillSettingsDialog.askAi')}
              </button>
            </div>
            {aiSuggestion ? (
              <p className="text-sm text-foreground">{aiSuggestion}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                {t('skillSettingsDialog.aiComingSoon')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('skillSettingsDialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onConfirm(value)}
          >
            {t('skillSettingsDialog.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
